import { http } from '../http';

const BASEURL = '/api/ai-call';

export type AiCallStatus = 'IN_PROGRESS' | 'PENDING_REVIEW' | 'SUBMITTED' | 'DISMISSED';
export type AiCallFormType = 'BULKY_ITEM_REMOVAL' | 'LIFT_COMPLAINT' | 'GENERAL';

export interface AiCallAddress {
  block?: string;
  street?: string;
  building?: string | null;
  postal?: string;
  full?: string;
}

/** Mirrors the official IEMS "Bulky Items Removal Request Form" */
export interface BulkyRemovalFormData {
  fullName?: string | null;
  email?: string | null;
  mobile?: string | null;
  telephone?: string | null;
  address?: AiCallAddress | null;
  floor?: string | null;
  unit?: string | null;
  appointmentDate?: string | null;
  items?: string[] | null;
  numItems?: number | null;
  remarks?: string | null;
  termsAccepted?: boolean | null;
  complete?: boolean;
  missingRequired?: string[];
  [key: string]: unknown;
}

export interface AiCallDialogue {
  speaker: string;
  message: string;
  timestamp: number | number[] | string;
}

/** Transfer to a human officer, as recorded by the bot */
export interface AiCallTransfer {
  to?: string;
  reason?: string;
  requestedAt?: string;
  /**
   * requested (operator asked, bot not yet acting) | handoff (AI telling the caller) | dialing |
   * answered | no-answer | busy | failed | canceled | expired (call ended before the hand-off) | unknown
   */
  status?: string;
  /** operator (Take over button) | agent (caller asked the AI) */
  initiatedBy?: string;
  note?: string;
  requestedBy?: number | null;
  requestedByName?: string | null;
  takeoverRequestedAt?: string;
  timeoutSeconds?: number;
  whisper?: string;
  durationSeconds?: number | null;
  completedAt?: string;
  error?: string;
  /** transcript of the caller/officer leg: pending | done | failed (absent = no recording) */
  transcript?: string;
  transcriptLines?: number;
  recordingSid?: string;
}

export interface AiCallVo {
  id: number;
  createdAt: number;
  updatedAt?: number;
  callSid: string;
  agent: string;
  callerNumber?: string | null;
  startedAt?: number | null;
  endedAt?: number | null;
  outcome?: string | null;
  formType: AiCallFormType;
  formTypeFormat?: string;
  formData?: BulkyRemovalFormData | null;
  bookingReference?: string | null;
  summaryTitle?: string | null;
  summary?: string | null;
  dialogues?: AiCallDialogue[] | null;
  transfer?: AiCallTransfer | null;
  status: AiCallStatus;
  statusFormat?: string;
  caseId?: number | null;
  caseNo?: string | null;
  /** workflow status of that case: OPEN | DISPATCHED | COLLECTED | CLOSED | CANCELLED */
  caseStatus?: string | null;
  reviewedBy?: number | null;
  reviewedByName?: string | null;
  reviewedAt?: number | null;
  reviewNote?: string | null;
  durationSeconds?: number | null;
  /** list endpoints, live calls only: number of transcript lines so far */
  turns?: number | null;
  /** list endpoints, live calls only: the latest transcript line */
  lastMessage?: AiCallDialogue | null;
}

export interface AiCallPending {
  count: number;
  latestId: number;
  latest?: AiCallVo | null;
  /** calls connected right now (newest first, no transcript) */
  live?: AiCallVo[] | null;
  liveCount?: number;
}

export interface AiCallSubmitResult {
  aiCallId: number;
  caseId: number;
  caseNo: string;
}

export const getAiCallPage = async (params: {
  page?: number;
  size?: number;
  status?: AiCallStatus;
  formType?: AiCallFormType;
}) => {
  return await http.get(`${BASEURL}/page`, { params });
};

export const getAiCall = async (id: number | string) => {
  return await http.get(`${BASEURL}/${id}`);
};

export const getAiCallPending = async () => {
  return await http.get(`${BASEURL}/pending`);
};

export const updateAiCallForm = async (
  id: number | string,
  formData: BulkyRemovalFormData,
) => {
  return await http.put(`${BASEURL}/${id}/form`, { formData });
};

export const submitAiCall = async (
  id: number | string,
  params: { formData?: BulkyRemovalFormData; priority?: string; note?: string },
) => {
  return await http.post(`${BASEURL}/${id}/submit`, params);
};

export const dismissAiCall = async (id: number | string, note?: string) => {
  return await http.post(`${BASEURL}/${id}/dismiss`, { note });
};

/** Operator takes a live call over: the bot hands the caller to the configured officer number */
export const takeoverAiCall = async (id: number | string) => {
  return await http.post(`${BASEURL}/${id}/takeover`, {});
};
